import type { BillingInterval, PaymentMethod, Plan } from "../../../shared/types.js";
import { api } from "./api.js";
import { applyOfferTokenToParams, loadPersistedCheckoutOfferToken, persistCheckoutOfferToken } from "./checkoutOfferSession.js";

const ACTIVE_ORDER_KEY = "pix_active_order_id";
export const PIX_CHARGE_TTL_MS = 15 * 60 * 1000;

export interface PixCheckoutContext {
  orderId: string;
  plan: Plan;
  interval: BillingInterval;
  email: string;
  /** Full name — restored when returning from PIX/Stripe. */
  fullName?: string;
  /** E.164 phone — restored when returning from PIX pending. */
  phone?: string;
  /** CPF digits (11) — restored when returning from PIX pending. */
  cpf?: string;
  /** Where "back" should land — defaults to public checkout. */
  returnTo?: "checkout" | "integrated";
  integratedToken?: string;
  /** Sales/preview token — preserved so the phone lock survives the round-trip. */
  salesToken?: string;
  /** Whether the phone field was locked (from the sales token). */
  phoneLocked?: boolean;
  /** Lead-recovery offer token — must survive PIX/Stripe back navigation. */
  offerToken?: string;
}

export interface PixOrderSnapshot {
  id: string;
  status: string;
  plan?: Plan;
  interval?: BillingInterval;
  payment_method?: PaymentMethod | string;
  context_kind?: string | null;
  app_return_url?: string | null;
  email?: string;
  final_cents?: number;
  recurring_cents?: number;
  /** What the provider charges every cycle after this one (see GET /orders/:id). */
  renewal_cents?: number;
  first_charge_at?: string | null;
  pix_emv?: string | null;
  pix_expires_at?: string | null;
  redirect?: string | null;
  /** Second paid order on a used offer link: refunded by hand, no access flow. */
  offer_already_redeemed?: boolean;
  /** Paid add-on keys this charge includes. */
  bump_keys?: string[];
}

/**
 * A pending PIX charge can be resumed only while it charges for exactly the
 * add-ons the buyer has selected now; otherwise a new checkout is needed.
 */
export function pixOrderMatchesBumps(
  order: Pick<PixOrderSnapshot, "bump_keys">,
  selectedKeys: Iterable<string>,
): boolean {
  const charged = [...new Set(order.bump_keys ?? [])].sort();
  const selected = [...new Set(selectedKeys)].sort();
  return charged.length === selected.length && charged.every((key, i) => key === selected[i]);
}

export function savePixCheckoutContext(ctx: PixCheckoutContext): void {
  if (ctx.offerToken) persistCheckoutOfferToken(ctx.offerToken);
  sessionStorage.setItem(ACTIVE_ORDER_KEY, ctx.orderId);
  sessionStorage.setItem(`pix_checkout_ctx_${ctx.orderId}`, JSON.stringify(ctx));
}

export function loadPixCheckoutContext(orderId: string): PixCheckoutContext | null {
  const raw = sessionStorage.getItem(`pix_checkout_ctx_${orderId}`);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as PixCheckoutContext;
    return {
      ...parsed,
      offerToken: parsed.offerToken || loadPersistedCheckoutOfferToken() || undefined,
    };
  } catch {
    return null;
  }
}

export function getStoredActivePixOrderId(): string | null {
  return sessionStorage.getItem(ACTIVE_ORDER_KEY);
}

export function clearPixCheckoutContext(orderId: string): void {
  sessionStorage.removeItem(`pix_emv_${orderId}`);
  sessionStorage.removeItem(`pix_checkout_ctx_${orderId}`);
  if (sessionStorage.getItem(ACTIVE_ORDER_KEY) === orderId) {
    sessionStorage.removeItem(ACTIVE_ORDER_KEY);
  }
}

/** True while the order is pending, PIX, and the charge has not expired. */
export function isPixOrderActive(order: PixOrderSnapshot): boolean {
  if (order.status !== "pending" || order.payment_method !== "openpix") return false;
  if (!order.pix_expires_at) return true;
  return new Date(order.pix_expires_at).getTime() > Date.now();
}

export function buildCheckoutUrl(ctx: PixCheckoutContext): string {
  const params = new URLSearchParams({
    plan: ctx.plan,
    interval: ctx.interval,
    method: "openpix",
    email: ctx.email,
    pix_order: ctx.orderId,
  });
  if (ctx.fullName) params.set("name", ctx.fullName);
  if (ctx.phone) params.set("whatsapp", ctx.phone);
  if (ctx.cpf) params.set("cpf", ctx.cpf.replace(/\D/g, ""));
  if (ctx.salesToken) params.set("sales_token", ctx.salesToken);
  if (ctx.phoneLocked) params.set("phone_locked", "1");
  applyOfferTokenToParams(params, ctx.offerToken);
  return `/checkout?${params.toString()}`;
}

export function buildPixBackUrl(ctx: PixCheckoutContext): string {
  if (ctx.returnTo === "integrated") {
    const params = new URLSearchParams();
    if (ctx.integratedToken) params.set("token", ctx.integratedToken);
    const q = params.toString();
    return q ? `/integrated?${q}` : "/integrated";
  }
  return buildCheckoutUrl(ctx);
}

export function pixExpiresInMs(
  order: Pick<PixOrderSnapshot, "pix_expires_at">,
  now = Date.now(),
): number | null {
  if (!order.pix_expires_at) return null;
  const at = new Date(order.pix_expires_at).getTime();
  if (!Number.isFinite(at)) return null;
  return Math.max(0, at - now);
}

/**
 * What the code on screen is worth now, by the server's order (PIT-546 b). `expired` covers an order
 * sync:expire-pix already marked expired while the page was in the background, whatever the local
 * clock still shows; it can get a new code. `closed` (canceled, failed) cannot: regenerate answers
 * order_canceled.
 */
export type PixCodeState = "active" | "expired" | "closed" | "paid";

export function pixCodeState(
  order: Pick<PixOrderSnapshot, "status" | "pix_expires_at"> | null,
  now = Date.now(),
): PixCodeState {
  if (!order) return "active";
  if (order.status === "paid") return "paid";
  if (order.status === "expired") return "expired";
  if (order.status !== "pending") return "closed";
  return pixExpiresInMs(order, now) === 0 ? "expired" : "active";
}

export function pendingCheckoutMatchesSelection(args: {
  pending: { plan: Plan; interval: BillingInterval; payment_method: string };
  plan: Plan;
  interval: BillingInterval;
  paymentMethod: PaymentMethod;
}): boolean {
  return (
    args.pending.plan === args.plan &&
    args.pending.interval === args.interval &&
    args.pending.payment_method === args.paymentMethod
  );
}

export function buildIntegratedPixBackUrl(token: string): string {
  const params = new URLSearchParams();
  if (token) params.set("token", token);
  const q = params.toString();
  return q ? `/integrated?${q}` : "/integrated";
}

/** Open PIX pending from /integrated — token survives popup navigation via query string. */
export function buildIntegratedPixPendingUrl(orderId: string, token: string): string {
  const params = new URLSearchParams({ from: "integrated", token });
  return `/pix/pending/${orderId}?${params.toString()}`;
}

export function resolvePixCheckoutContext(args: {
  orderId: string;
  searchParams: URLSearchParams;
  stored: PixCheckoutContext | null;
  order?: Pick<PixOrderSnapshot, "plan" | "interval" | "email" | "context_kind"> | null;
}): PixCheckoutContext | null {
  const fromIntegrated = args.searchParams.get("from") === "integrated";
  const integratedToken = args.searchParams.get("token") ?? undefined;

  if (args.stored) {
    if (fromIntegrated && integratedToken) {
      return { ...args.stored, returnTo: "integrated", integratedToken };
    }
    return {
      ...args.stored,
      offerToken: args.stored.offerToken || loadPersistedCheckoutOfferToken() || undefined,
    };
  }

  if (!args.order?.plan || !args.order.interval || !args.order.email) return null;

  if (fromIntegrated && integratedToken) {
    return {
      orderId: args.orderId,
      plan: args.order.plan,
      interval: args.order.interval,
      email: args.order.email,
      returnTo: "integrated",
      integratedToken,
    };
  }
  // An existing customer's change has no lead checkout to go back to, and GET /orders/:id masks its
  // email (PIT-535 review): a back link built from it would prefill "j***@…".
  if (args.order.context_kind === "existing_customer") return null;

  return {
    orderId: args.orderId,
    plan: args.order.plan,
    interval: args.order.interval,
    email: args.order.email,
    offerToken: loadPersistedCheckoutOfferToken() || undefined,
  };
}

export function resolveIntegratedPixReturnUrl(
  ctx: PixCheckoutContext | null,
  searchParams?: URLSearchParams,
): string | null {
  if (ctx?.returnTo === "integrated" && ctx.integratedToken) {
    return buildIntegratedPixBackUrl(ctx.integratedToken);
  }
  const params = searchParams ?? new URLSearchParams(window.location.search);
  const token = params.get("token");
  if (params.get("from") === "integrated" && token) {
    return buildIntegratedPixBackUrl(token);
  }
  return null;
}

export function formatPixTimeLeft(ms: number): string {
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
}

export function loadStoredPixEmv(orderId: string): string {
  try {
    return sessionStorage.getItem(`pix_emv_${orderId}`) ?? "";
  } catch {
    return "";
  }
}

/** Best available PIX copy-paste code from React state, order snapshot, or sessionStorage. */
export function resolvePixEmv(
  orderId: string,
  emv: string,
  order?: Pick<PixOrderSnapshot, "pix_emv"> | null,
): string {
  return emv || order?.pix_emv || loadStoredPixEmv(orderId) || "";
}

export async function copyPixEmvToClipboard(
  code: string,
  textarea?: HTMLTextAreaElement | null,
): Promise<boolean> {
  const text = code.trim();
  if (!text) return false;

  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
  }

  const el = textarea ?? document.createElement("textarea");
  const created = el !== textarea;
  if (created) {
    el.value = text;
    el.setAttribute("readonly", "");
    el.style.position = "fixed";
    el.style.left = "-9999px";
    document.body.appendChild(el);
  }
  try {
    el.focus();
    el.select();
    el.setSelectionRange(0, text.length);
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    if (created) document.body.removeChild(el);
  }
}

export async function regeneratePixOrder(orderId: string): Promise<{ pix_emv: string; pix_expires_at: string }> {
  return api<{ pix_emv: string; pix_expires_at: string }>(`/api/public/orders/${orderId}/regenerate-pix`, {
    method: "POST",
  });
}

/**
 * Fetch order; if PIX expired but still pending, create a fresh 15-minute charge first. With
 * `reviveExpired`, also a new customer's order the expiry sweep already marked `expired` (a recovery
 * link opened later lands on a working QR instead of an "expired" box).
 */
export async function ensureActivePixOrder(
  orderId: string,
  opts: { reviveExpired?: boolean } = {},
): Promise<PixOrderSnapshot> {
  const fetchOrder = () => api<PixOrderSnapshot>(`/api/public/orders/${orderId}`);
  let order = await fetchOrder();
  if (order.status === "paid") return order;
  const revivable = opts.reviveExpired && order.status === "expired" && order.context_kind === "new_customer";
  if (order.payment_method !== "openpix" || (order.status !== "pending" && !revivable)) return order;
  if (isPixOrderActive(order)) return order;

  const reg = await regeneratePixOrder(orderId);
  if (reg.pix_emv) sessionStorage.setItem(`pix_emv_${orderId}`, reg.pix_emv);
  return {
    ...order,
    // The server puts a regenerated order back to pending.
    status: "pending",
    pix_emv: reg.pix_emv,
    pix_expires_at: reg.pix_expires_at,
  };
}
